import { useCallback, useEffect, useRef, useState } from 'react'

export const RESEND_SECONDS = 60

const IDLE = { status: 'idle', code: '', error: '', remainingSeconds: 0, sent: false }

/**
 * The send-a-code / enter-the-code state machine, shared by every flow that
 * asks someone to prove they own a contact detail — the consent form and
 * public questionnaires.
 *
 * Callers supply the two requests, since the endpoints and the identity they
 * authenticate with differ; everything around them (per-method status, the
 * six-digit input, errors, the resend countdown, and clearing a verification
 * when the contact detail is edited) is the same and lives here.
 *
 * `requestCode(method, done, fail)` and `submitCode(method, code, done, fail)`
 * follow the api client's success/fail callback convention. Read a method's
 * state with `stateFor` — which methods apply may not be known until the form
 * has loaded, so states are seeded lazily rather than up front.
 */
export default function useContactVerification ({
  requestCode,
  submitCode,
  onVerified,
  onEvent
}) {
  const [states, setStates] = useState({})
  const countdowns = useRef({})

  const patch = useCallback((method, changes) => {
    setStates(prev => ({ ...prev, [method]: { ...(prev[method] || IDLE), ...changes } }))
  }, [])

  const startCountdown = useCallback(method => {
    if (countdowns.current[method]) clearInterval(countdowns.current[method])
    countdowns.current[method] = setInterval(() => {
      setStates(prev => {
        const remaining = (prev[method]?.remainingSeconds || 0) - 1
        if (remaining <= 0) {
          clearInterval(countdowns.current[method])
          countdowns.current[method] = null
        }
        return {
          ...prev,
          [method]: { ...prev[method], remainingSeconds: Math.max(0, remaining) }
        }
      })
    }, 1000)
  }, [])

  useEffect(() => () => {
    Object.values(countdowns.current).forEach(i => { if (i) clearInterval(i) })
  }, [])

  const request = useCallback(method => {
    patch(method, { status: 'sending', error: '' })
    onEvent?.('verificationRequested', { method })
    requestCode(
      method,
      () => {
        patch(method, { status: 'sent', sent: true, remainingSeconds: RESEND_SECONDS })
        startCountdown(method)
      },
      // A failed *resend* must not tear down the code entry: the respondent may
      // already hold a working code (a rate-limited resend is the common case).
      err => setStates(prev => {
        const current = prev[method] || IDLE
        return {
          ...prev,
          [method]: { ...current, status: current.sent ? 'sent' : 'idle', error: err.message }
        }
      })
    )
  }, [patch, requestCode, startCountdown, onEvent])

  const verify = useCallback(method => {
    const code = states[method]?.code
    if (!code || code.length !== 6) return
    patch(method, { status: 'verifying', error: '' })
    onEvent?.('verificationCodeEntered', { method })
    submitCode(
      method,
      code,
      () => {
        onEvent?.('verificationSucceeded', { method })
        patch(method, { status: 'verified', error: '' })
        onVerified?.(method)
      },
      err => {
        onEvent?.('verificationFailed', { method, error: err.message })
        patch(method, { status: 'sent', error: err.message })
      }
    )
  }, [states, patch, submitCode, onVerified, onEvent])

  const setCode = useCallback((method, code) => patch(method, { code }), [patch])

  // Editing a contact detail invalidates the code that was sent to the old
  // value, so the participant is put back to the start for that method.
  const reset = useCallback(method => {
    if (countdowns.current[method]) {
      clearInterval(countdowns.current[method])
      countdowns.current[method] = null
    }
    setStates(prev => ({ ...prev, [method]: { ...IDLE } }))
  }, [])

  const stateFor = useCallback(method => states[method] || IDLE, [states])

  return { stateFor, request, verify, setCode, reset }
}
